import { useEffect, useRef, useState } from 'react';
import { MAX_INJECTION_COUNT } from './injectionCount.config';

const choices = Array.from({ length: MAX_INJECTION_COUNT }, (_, i) => i + 1);

type ChevronDownProps = {
  className?: string;
};

const ChevronDown = ({ className = '' }: ChevronDownProps) => (
  <svg
    width="14"
    height="9"
    viewBox="0 0 14 9"
    fill="none"
    className={className}
  >
    <path
      d="M1 1.5L7 7.5L13 1.5"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type InjectionCountDropdownProps = {
  value: number | null;
  onChange: (value: number) => void;
};

export const InjectionCountDropdown = ({
  value,
  onChange,
}: InjectionCountDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full flex items-center justify-between bg-grey-900 text-grey-0 text-p1 rounded-xl px-24 py-16 outline-none"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={value === null ? 'text-grey-500' : ''}>
          {value ?? '\u2014'}
        </span>
        <ChevronDown
          className={`text-grey-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className="absolute z-10 left-0 right-0 mt-8 bg-grey-900 rounded-xl py-8 shadow-xl overflow-hidden"
        >
          {choices.map((choice) => {
            const isSelected = value === choice;
            return (
              <li key={choice}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(choice);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-24 py-16 text-p1 ${
                    isSelected
                      ? 'bg-grey-800 text-grey-0 font-semibold'
                      : 'text-grey-0 lg:hover:bg-grey-800'
                  }`}
                >
                  {choice}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
